import {
  Button,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import type { PhoneNumber } from 'lib-shared-legacy-types';
import {
  formatInternational,
  getCountryCodeFromPhoneNumber,
} from 'lib-shared-phone-numbers';
import { FC, useCallback, useMemo, useState } from 'react';

import type { FlowCallActionT } from 'components/powerCallFlow/api/types';
import type { WithFlowsInjectedPropsT } from 'hocs';
import { FlowT } from 'hocs/flows/api/types';

import FlagSelect, { ItemT } from '../flagSelect';
import { useNumberTable } from '../numberTableContext';

import styles from './moveFlowModal.module.scss';

type PropsT = {
  isShown: boolean;
  number: PhoneNumber;
  selectedFlow: FlowT<FlowCallActionT> | null;
  onClose: () => void;
} & WithFlowsInjectedPropsT<FlowCallActionT>;

const MoveFlowModal: FC<PropsT> = ({
  isShown,
  number,
  selectedFlow,
  flows,
  onClose,
}) => {
  const { fetchNumbers, phoneNumbers } = useNumberTable();
  const { items, getItemsAsyncStatus } = phoneNumbers;
  const { e164_format: currentNumber } = number;

  const isGetItemsLoading = getItemsAsyncStatus === 'loading';

  const [isLoading, setIsLoading] = useState(false);
  const [selectedNumber, setSelectedItem] = useState(currentNumber);

  const onSelect = useCallback((item: ItemT) => {
    const { id } = item;

    setSelectedItem(id);
  }, []);

  const onMove = useCallback(() => {
    if (!selectedFlow) {
      return;
    }

    const { id } = selectedFlow;

    setIsLoading(true);

    flows
      .update(id, { ...selectedFlow, location: selectedNumber })
      .then(() => setIsLoading(false))
      .then(() => onClose())
      .then(() => fetchNumbers());
  }, [
    flows,
    selectedFlow,
    onClose,
    fetchNumbers,
    setIsLoading,
    selectedNumber,
  ]);

  const options = useMemo(
    () =>
      items.map(({ e164_format, name }) => {
        const flag = getCountryCodeFromPhoneNumber(e164_format) || '';
        const number = formatInternational(e164_format);

        return {
          id: e164_format,
          flag,
          name: `${number} ${name ? `(${name})` : ''}`,
        };
      }),
    [items],
  );

  if (!selectedFlow) {
    return null;
  }

  return (
    <Modal isOpen={isShown} onClose={onClose}>
      <ModalHead title="Move to another number" onClose={onClose} />
      <ModalBody className={styles.modal}>
        <label className={styles.label}>Your phone numbers</label>
        <FlagSelect
          items={options}
          isLoading={isGetItemsLoading}
          selectedItem={selectedNumber}
          onSelect={onSelect}
        />
      </ModalBody>
      <ModalFoot className={styles.foot}>
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
        <Button isLoading={isLoading} onClick={onMove}>
          Move
        </Button>
      </ModalFoot>
    </Modal>
  );
};

export default MoveFlowModal;
